import React, { useState, useEffect } from 'react';
import { BasicPage } from '../components/BasicPage';
import { useAuth, apiFetch } from '../hooks/useAuth';
import { firebaseConfig } from '../firebase';
import {
    Box, Alert, Button, TextField, Switch, FormControlLabel,
    Paper, Typography, Stack, IconButton, Divider, Dialog,
    DialogTitle, DialogContent, DialogActions, Chip, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Autocomplete, CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Settings as SettingsIcon,
    Save as SaveIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Cart Configurator
 * SuperAdmin interface to manage custom cart field configurations per user email
 */
export default function CartConfigurator() {
    const { claims, token } = useAuth();
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Check if user is SuperAdmin
    const isSuperAdmin = claims?.role === 'SuperAdmin';

    useEffect(() => {
        if (isSuperAdmin) {
            loadConfigs();
            loadUsers();
        }
    }, [isSuperAdmin]);

    const loadUsers = async () => {
        try {
            setLoadingUsers(true);
            const response = await apiFetch('/app/users/');
            const data = await response.json();
            setUsers(data.users || []);
        } catch (err) {
            console.error('Error loading users:', err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const loadConfigs = async () => {
        try {
            setLoading(true);
            const snapshot = await getDocs(collection(db, 'cart_configs'));
            const configList = [];
            snapshot.forEach(doc => {
                configList.push({ id: doc.id, ...doc.data() });
            });
            setConfigs(configList);
            setError(null);
        } catch (err) {
            console.error('Error loading configs:', err);
            setError('Fehler beim Laden der Konfigurationen: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (config) => {
        setEditingConfig(config);
        setDialogOpen(true);
    };

    const handleNew = () => {
        setEditingConfig({
            id: '',
            userEmail: '',
            userName: '',
            customerNumber: '',
            active: true,
            fields: {
                recipientEmail: {
                    enabled: false,
                    required: false,
                    label: 'E-Mail des Empfängers',
                    placeholder: 'empfaenger@example.com',
                    type: 'email'
                },
                location: {
                    enabled: false,
                    required: false,
                    label: 'Standort',
                    type: 'dropdown',
                    options: []
                },
                customField1: {
                    enabled: false,
                    required: false,
                    label: 'Zusatzfeld 1',
                    type: 'text'
                }
            }
        });
        setDialogOpen(true);
    };

    const handleDelete = async (userEmail) => {
        if (!window.confirm(`Konfiguration für ${userEmail} wirklich löschen?`)) {
            return;
        }
        
        try {
            await deleteDoc(doc(db, 'cart_configs', userEmail));
            setSuccess(`Konfiguration für ${userEmail} gelöscht`);
            loadConfigs();
        } catch (err) {
            setError('Fehler beim Löschen: ' + err.message);
        }
    };

    if (!isSuperAdmin) {
        return (
            <BasicPage title="Warenkorb-Konfigurator" icon={<SettingsIcon />}>
                <Alert severity="error">
                    Zugriff verweigert. Nur für Super-Administratoren.
                </Alert>
            </BasicPage>
        );
    }

    return (
        <BasicPage title="Warenkorb-Konfigurator" icon={<SettingsIcon />}>
            <Box sx={{ p: 2 }}>
                <Stack spacing={3}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Kundenspezifische Warenkorb-Felder
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleNew}
                        >
                            Neue Konfiguration
                        </Button>
                    </Box>

                    {/* Messages */}
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
                    {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

                    {/* Info Box */}
                    <Alert severity="info">
                        Hier können Sie für bestimmte Benutzer zusätzliche Pflichtfelder im Warenkorb aktivieren.
                        Die Konfiguration wird automatisch anhand der E-Mail-Adresse zugeordnet.
                    </Alert>

                    {/* Configs List */}
                    {loading ? (
                        <Typography>Lade Konfigurationen...</Typography>
                    ) : configs.length === 0 ? (
                        <Paper sx={{ p: 3, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                Noch keine Konfigurationen vorhanden.
                            </Typography>
                        </Paper>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Benutzer E-Mail</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Aktive Felder</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Aktionen</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {configs.map((config) => {
                                        const activeFields = Object.entries(config.fields || {})
                                            .filter(([key, field]) => field.enabled)
                                            .map(([key, field]) => field.label);
                                        
                                        return (
                                            <TableRow key={config.id}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {config.userEmail || config.id}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{config.userName || config.customerName || '-'}</TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                                        {activeFields.length > 0 ? (
                                                            activeFields.map((label, idx) => (
                                                                <Chip key={idx} label={label} size="small" />
                                                            ))
                                                        ) : (
                                                            <Chip label="Keine" size="small" color="default" />
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={config.active ? 'Aktiv' : 'Inaktiv'}
                                                        color={config.active ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEdit(config)}
                                                        color="primary"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(config.userEmail || config.id)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Stack>
            </Box>

            {/* Edit Dialog */}
            <ConfigDialog
                open={dialogOpen}
                config={editingConfig}
                users={users}
                loadingUsers={loadingUsers}
                onClose={() => {
                    setDialogOpen(false);
                    setEditingConfig(null);
                }}
                onSave={async (savedConfig) => {
                    try {
                        // Use userEmail as document ID
                        await setDoc(doc(db, 'cart_configs', savedConfig.userEmail), {
                            ...savedConfig,
                            updatedAt: new Date()
                        });
                        setSuccess('Konfiguration gespeichert');
                        setDialogOpen(false);
                        setEditingConfig(null);
                        loadConfigs();
                    } catch (err) {
                        setError('Fehler beim Speichern: ' + err.message);
                    }
                }}
            />
        </BasicPage>
    );
}

/**
 * Dialog for editing cart configuration
 */
function ConfigDialog({ open, config, users, loadingUsers, onClose, onSave }) {
    const [formData, setFormData] = useState(null);
    const [locationInput, setLocationInput] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        if (config) {
            setFormData({ ...config });
            // Find the user if editing existing config
            if (config.userEmail && users) {
                const user = users.find(u => u.email === config.userEmail);
                setSelectedUser(user || null);
            }
        }
    }, [config, users]);

    if (!formData) return null;

    const handleUserChange = (event, newValue) => {
        setSelectedUser(newValue);
        if (newValue) {
            setFormData(prev => ({
                ...prev,
                userEmail: newValue.email,
                userName: newValue.displayName,
                customerNumber: newValue.customClaims?.aid || ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                userEmail: '',
                userName: '',
                customerNumber: ''
            }));
        }
    };

    const handleFieldChange = (fieldKey, property, value) => {
        setFormData(prev => ({
            ...prev,
            fields: {
                ...prev.fields,
                [fieldKey]: {
                    ...prev.fields[fieldKey],
                    [property]: value
                }
            }
        }));
    };

    const handleAddLocation = () => {
        if (!locationInput.trim()) return;
        
        const newOption = {
            id: locationInput.replace(/\s+/g, '_').toLowerCase(),
            label: locationInput.trim()
        };
        
        setFormData(prev => ({
            ...prev,
            fields: {
                ...prev.fields,
                location: {
                    ...prev.fields.location,
                    options: [...(prev.fields.location.options || []), newOption]
                }
            }
        }));
        
        setLocationInput('');
    };

    const handleRemoveLocation = (index) => {
        setFormData(prev => ({
            ...prev,
            fields: {
                ...prev.fields,
                location: {
                    ...prev.fields.location,
                    options: prev.fields.location.options.filter((_, i) => i !== index)
                }
            }
        }));
    };

    const handleSave = () => {
        if (!formData.userEmail) {
            alert('Bitte wählen Sie einen Benutzer aus');
            return;
        }
        onSave(formData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {formData.id ? 'Konfiguration bearbeiten' : 'Neue Konfiguration'}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    {/* Basic Info */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle2" gutterBottom>Grundinformationen</Typography>
                            <Stack spacing={2}>
                                <Autocomplete
                                    options={users || []}
                                    value={selectedUser}
                                    onChange={handleUserChange}
                                    getOptionLabel={(option) => `${option.email} - ${option.displayName || 'No Name'}`}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Stack>
                                                <Typography variant="body2">{option.email}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.displayName} {option.customClaims?.cny ? `(${option.customClaims.cny})` : ''}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Benutzer auswählen"
                                            required
                                            helperText="Wählen Sie den Benutzer aus, für den die Custom Cart Felder aktiviert werden sollen"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    disabled={!!formData.id}
                                    fullWidth
                                    loading={loadingUsers}
                                />
                                
                                {selectedUser && (
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="caption" color="text.secondary">Ausgewählter Benutzer:</Typography>
                                        <Typography variant="body2"><strong>Name:</strong> {formData.userName}</Typography>
                                        <Typography variant="body2"><strong>Email:</strong> {formData.userEmail}</Typography>
                                        {formData.customerNumber && (
                                            <Typography variant="body2"><strong>Kunden-Nr.:</strong> {formData.customerNumber}</Typography>
                                        )}
                                    </Box>
                                )}
                                
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.active}
                                            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                        />
                                    }
                                    label="Konfiguration aktiv"
                                />
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Email Field */}
                    <Card variant="outlined">
                        <CardContent>
                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle2">E-Mail Empfänger Feld</Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.fields.recipientEmail?.enabled}
                                                onChange={(e) => handleFieldChange('recipientEmail', 'enabled', e.target.checked)}
                                            />
                                        }
                                        label="Aktiviert"
                                    />
                                </Box>
                                {formData.fields.recipientEmail?.enabled && (
                                    <>
                                        <TextField
                                            label="Feld-Bezeichnung"
                                            value={formData.fields.recipientEmail.label}
                                            onChange={(e) => handleFieldChange('recipientEmail', 'label', e.target.value)}
                                            fullWidth
                                            size="small"
                                        />
                                        <TextField
                                            label="Platzhalter"
                                            value={formData.fields.recipientEmail.placeholder}
                                            onChange={(e) => handleFieldChange('recipientEmail', 'placeholder', e.target.value)}
                                            fullWidth
                                            size="small"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.fields.recipientEmail.required}
                                                    onChange={(e) => handleFieldChange('recipientEmail', 'required', e.target.checked)}
                                                />
                                            }
                                            label="Pflichtfeld"
                                        />
                                    </>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Location Field */}
                    <Card variant="outlined">
                        <CardContent>
                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle2">Standort Dropdown</Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.fields.location?.enabled}
                                                onChange={(e) => handleFieldChange('location', 'enabled', e.target.checked)}
                                            />
                                        }
                                        label="Aktiviert"
                                    />
                                </Box>
                                {formData.fields.location?.enabled && (
                                    <>
                                        <TextField
                                            label="Feld-Bezeichnung"
                                            value={formData.fields.location.label}
                                            onChange={(e) => handleFieldChange('location', 'label', e.target.value)}
                                            fullWidth
                                            size="small"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.fields.location.required}
                                                    onChange={(e) => handleFieldChange('location', 'required', e.target.checked)}
                                                />
                                            }
                                            label="Pflichtfeld"
                                        />
                                        <Divider />
                                        <Typography variant="body2" color="text.secondary">
                                            Standorte (Dropdown-Optionen):
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <TextField
                                                label="Neuer Standort"
                                                value={locationInput}
                                                onChange={(e) => setLocationInput(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleAddLocation()}
                                                size="small"
                                                fullWidth
                                            />
                                            <Button onClick={handleAddLocation} variant="outlined">
                                                <AddIcon />
                                            </Button>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {formData.fields.location.options?.map((option, index) => (
                                                <Chip
                                                    key={index}
                                                    label={option.label}
                                                    onDelete={() => handleRemoveLocation(index)}
                                                    size="small"
                                                />
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} startIcon={<CloseIcon />}>
                    Abbrechen
                </Button>
                <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
                    Speichern
                </Button>
            </DialogActions>
        </Dialog>
    );
}

